import { queryOptions } from '@tanstack/react-query';

import {
  fetchConversation,
  fetchConversations,
} from '@/integration/services/integration-api.js';
import type { Conversation } from '@/integration/types.js';
import { apiFetch } from '@/lib/api-fetch.js';

/**
 * TanStack Query factory for the conversation inbox.
 *
 * The list comes from the integration backend at `/v1/conversations` —
 * x-user-id auth (set by `integration-api.ts` via the Teams SDK).
 *
 * Usage:
 *   const { data } = useQuery(conversationsQueryOptions());
 */
export function conversationsQueryOptions(search?: string) {
  return queryOptions({
    queryKey: ['conversations', search ?? ''],
    queryFn: async (): Promise<Conversation[]> => fetchConversations(search),
    staleTime: 15_000,
  });
}

/** Authoritative conversation detail, including lifecycle and management capability. */
export function conversationQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['conversation', id],
    queryFn: async (): Promise<Conversation> => fetchConversation(id),
    staleTime: 15_000,
  });
}

/**
 * Records that a diagram preview of an assistant message was saved as a diagram, so the message
 * offers Open in Diagrams after a reload. The link grants nothing: conversation-service checks that
 * the caller can read the diagram and stores only the pointer.
 */
export async function linkDiagramToMessage(input: {
  conversationId: string;
  messageId: string;
  diagramId: string;
  specIndex: number;
}): Promise<void> {
  const response = await apiFetch(
    `/v1/conversations/${encodeURIComponent(input.conversationId)}/messages/${encodeURIComponent(input.messageId)}/diagram-link`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        diagram_id: input.diagramId,
        spec_index: input.specIndex,
      }),
    },
  );
  if (!response.ok) {
    throw new Error(`Could not link the diagram (${response.status})`);
  }
}

/**
 * Records that a dashboard snapshot of an assistant message was saved to Dashboards, so the message
 * offers Open instead of Save after a reload. conversation-service answers 404 when the message has no
 * dashboard at `specIndex`.
 */
export async function linkDashboardToMessage(input: {
  conversationId: string;
  messageId: string;
  dashboardId: string;
  specIndex: number;
  name: string;
}): Promise<void> {
  const response = await apiFetch(
    `/v1/conversations/${encodeURIComponent(input.conversationId)}/messages/${encodeURIComponent(input.messageId)}/dashboard-link`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        dashboard_id: input.dashboardId,
        spec_index: input.specIndex,
        name: input.name,
      }),
    },
  );
  if (!response.ok) {
    throw new Error(`Could not link the dashboard (${response.status})`);
  }
}
